import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { tasksAtom } from '../v0/taskAtoms';
import { Task } from '../../types/task';
import { sortTasksByTime } from '../../utils/taskSortUtils';
import { shouldTaskAppearOnDate, isTaskNaturallyOnDate, isTaskShownByDoneAt } from '../../utils/taskTimeUtils';

/**
 * TaskV2 页面状态管理
 */

// TaskV2 的核心状态类型
export interface TaskV2State {
  currentDate: Date;
  specialMode: 'past' | 'future' | 'notime' | 'all' | 'recent' | null;
  viewMode: 'list' | 'timeline' | 'quadrant';
  showNoTimeTasks: boolean;
  stackMode: boolean; // 新增：时间轴堆叠模式
}

// 默认状态
const defaultTaskV2State: TaskV2State = {
  currentDate: new Date(),
  specialMode: null,
  viewMode: 'list',
  showNoTimeTasks: false,
  stackMode: false
};

// 核心状态原子 - 统一管理所有 TaskV2 状态
const taskV2StateAtom = atom<TaskV2State>(defaultTaskV2State);

// 持久化的视图模式
const persistedViewModeAtom = atomWithStorage<'list' | 'timeline' | 'quadrant'>('taskV2ViewMode', 'list');

// 持久化的堆叠模式
const persistedStackModeAtom = atomWithStorage<boolean>('taskV2StackMode', false);

// 统一的状态管理原子 - 这是唯一的状态更新入口
export const taskV2UnifiedStateAtom = atom(
  (get) => {
    const state = get(taskV2StateAtom);
    const persistedViewMode = get(persistedViewModeAtom);
    const persistedStackMode = get(persistedStackModeAtom);

    // 合并持久化的视图模式和堆叠模式
    return {
      ...state,
      viewMode: persistedViewMode,
      stackMode: persistedStackMode
    };
  },
  (get, set, update: Partial<TaskV2State> | ((prev: TaskV2State) => Partial<TaskV2State>)) => {
    const currentState = get(taskV2StateAtom);
    const persistedViewMode = get(persistedViewModeAtom);
    const persistedStackMode = get(persistedStackModeAtom);

    const fullCurrentState = {
      ...currentState,
      viewMode: persistedViewMode,
      stackMode: persistedStackMode
    };

    const newState = typeof update === 'function'
      ? { ...fullCurrentState, ...update(fullCurrentState) }
      : { ...fullCurrentState, ...update };

    // 状态验证和自动修正
    const validatedState = validateAndFixState(newState);

    // 更新状态
    set(taskV2StateAtom, {
      currentDate: validatedState.currentDate,
      specialMode: validatedState.specialMode,
      showNoTimeTasks: validatedState.showNoTimeTasks,
      viewMode: validatedState.viewMode,
      stackMode: validatedState.stackMode
    });

    // 持久化视图模式
    if (validatedState.viewMode !== persistedViewMode) {
      set(persistedViewModeAtom, validatedState.viewMode);
    }

    // 持久化堆叠模式
    if (validatedState.stackMode !== persistedStackMode) {
      set(persistedStackModeAtom, validatedState.stackMode);
    }
  }
);

// 状态验证和自动修正函数
function validateAndFixState(state: TaskV2State): TaskV2State {
  const correctedState = { ...state };

  // 规则1：特殊模式下强制使用列表模式
  if (correctedState.specialMode && correctedState.viewMode === 'timeline') {
    correctedState.viewMode = 'list';
  }

  // 规则2：确保日期是有效的
  if (!(correctedState.currentDate instanceof Date) || isNaN(correctedState.currentDate.getTime())) {
    correctedState.currentDate = new Date();
  }

  return correctedState;
}

// 便捷的派生原子 - 向后兼容
export const taskV2CurrentDateAtom = atom(
  (get) => get(taskV2UnifiedStateAtom).currentDate,
  (get, set, newDate: Date) => {
    set(taskV2UnifiedStateAtom, { currentDate: newDate });
  }
);

export const taskV2ViewModeAtom = atom(
  (get) => get(taskV2UnifiedStateAtom).viewMode,
  (get, set, newMode: 'list' | 'timeline' | 'quadrant') => {
    set(taskV2UnifiedStateAtom, { viewMode: newMode });
  }
);

export const taskV2ShowNoTimeTasksAtom = atom(
  (get) => get(taskV2UnifiedStateAtom).showNoTimeTasks,
  (get, set, show: boolean) => {
    set(taskV2UnifiedStateAtom, { showNoTimeTasks: show });
  }
);

export const taskV2SpecialModeAtom = atom(
  (get) => get(taskV2UnifiedStateAtom).specialMode,
  (get, set, mode: 'past' | 'future' | 'notime' | 'all' | 'recent' | null) => {
    set(taskV2UnifiedStateAtom, { specialMode: mode });
  }
);

export const taskV2StackModeAtom = atom(
  (get) => get(taskV2UnifiedStateAtom).stackMode,
  (get, set, stackMode: boolean) => {
    set(taskV2UnifiedStateAtom, { stackMode });
  }
);

// 键盘导航状态 - 使用视觉位置索引，确保稳定性
export const taskV2FocusedIndexAtom = atom<number>(-1); // -1 表示没有选中任何任务

// 派生原子：获取当前焦点任务
export const taskV2FocusedTaskAtom = atom((get) => {
  const focusedIndex = get(taskV2FocusedIndexAtom);
  const visualTasks = get(taskV2VisualTaskListAtom);

  // 边界检查：索引越界时返回null
  if (focusedIndex < 0 || focusedIndex >= visualTasks.length) {
    return null;
  }

  return visualTasks[focusedIndex];
});

// 向后兼容：提供任务ID接口（派生状态）
export const taskV2FocusedTaskIdAtom = atom((get) => {
  const focusedTask = get(taskV2FocusedTaskAtom);
  return focusedTask?.id || null;
});

// 视觉任务列表原子（用于键盘导航）
export const taskV2VisualTaskListAtom = atom((get) => {
  const allTasks = get(tasksAtom);
  const currentDate = get(taskV2CurrentDateAtom);
  const specialMode = get(taskV2SpecialModeAtom);
  const temporaryVisibleTasks = get(taskV2TemporaryVisibleTasksAtom);

  if (!allTasks || allTasks.length === 0 || specialMode) {
    return get(taskV2CurrentTasksAtom); // 特殊模式下使用原逻辑
  }

  // 分类任务（与 TaskV2List 完全一致）
  const natural: Task[] = [];
  const completedToday: Task[] = [];
  const temporary: Task[] = [];

  allTasks.forEach((task) => {
    const isNaturallyToday = isTaskNaturallyOnDate(task, currentDate);
    const isCompletedToday = isTaskShownByDoneAt(task, currentDate);
    const isTemporary = temporaryVisibleTasks.has(task.id);

    if (isNaturallyToday) {
      // 真正属于今天的任务（最高优先级）
      natural.push(task);
    } else if (isCompletedToday) {
      // 完成于今天的其他日期任务（包括 undone 后的任务）
      completedToday.push(task);
    } else if (isTemporary) {
      // 临时缓存的任务（只有既不自然属于今天，也不是完成于今天的任务）
      temporary.push(task);
    }
  });

  // 统一排序逻辑（与 TaskV2List 完全一致）
  const sortedNatural = [...natural].sort((a, b) => {
    if (a.done !== b.done) return a.done ? 1 : -1;
    const aTime = a.startAt || a.dueAt;
    const bTime = b.startAt || b.dueAt;
    if (aTime && bTime) return new Date(aTime).getTime() - new Date(bTime).getTime();
    if (aTime && !bTime) return -1;
    if (!aTime && bTime) return 1;
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });

  const sortedCompletedToday = [...completedToday].sort((a, b) => {
    // 1. 完成状态：未完成在前
    if (a.done !== b.done) {
      return a.done ? 1 : -1;
    }

    // 2. 对于已完成的任务，按完成时间倒序（最近完成的在前）
    if (a.done && b.done && a.doneAt && b.doneAt) {
      return new Date(b.doneAt).getTime() - new Date(a.doneAt).getTime();
    }

    // 3. 对于未完成的任务，按创建时间排序
    if (!a.done && !b.done) {
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    }

    return 0;
  });

  const sortedTemporary = [...temporary].sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  // 返回完整的视觉顺序：自然任务 + 临时任务（分割线下方顶部）+ 完成于今天
  return [...sortedNatural, ...sortedTemporary, ...sortedCompletedToday];
});

// 向后兼容的索引原子（直接返回主索引）
export const taskV2FocusedTaskIndexAtom = atom((get) => {
  return get(taskV2FocusedIndexAtom);
});

// 临时显示状态：记录在当前会话中被操作过的任务，让它们继续显示在当前日期
export const taskV2TemporaryVisibleTasksAtom = atom<Set<string>>(new Set<string>());

// 高级操作原子
export const taskV2ActionsAtom = atom(
  null,
  (get, set) => ({
    // 跳转到今天 - 清除特殊模式，设置为今天
    goToToday: () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      set(taskV2UnifiedStateAtom, {
        currentDate: today,
        specialMode: null
      });
      set(taskV2FocusedIndexAtom, -1); // 重置选中状态
      set(taskV2TemporaryVisibleTasksAtom, new Set()); // 清理临时状态
    },

    // 切换到特殊模式
    setSpecialMode: (mode: 'past' | 'future' | 'notime' | 'all' | 'recent') => {
      set(taskV2UnifiedStateAtom, {
        specialMode: mode,
        viewMode: 'list' // 特殊模式强制列表模式
      });
      set(taskV2FocusedIndexAtom, -1); // 重置选中状态
      set(taskV2TemporaryVisibleTasksAtom, new Set()); // 清理临时状态
    },

    // 切换到日期模式
    setDateMode: (date: Date) => {
      set(taskV2UnifiedStateAtom, {
        currentDate: date,
        specialMode: null
      });
      set(taskV2FocusedIndexAtom, -1); // 重置选中状态
      set(taskV2TemporaryVisibleTasksAtom, new Set()); // 清理临时状态
    },

    // 切换视图模式（三模式循环切换）
    toggleViewMode: () => {
      const currentState = get(taskV2UnifiedStateAtom);

      if (currentState.specialMode) {
        // 特殊模式下只允许 list 和 quadrant 模式
        set(taskV2UnifiedStateAtom, {
          viewMode: currentState.viewMode === 'list' ? 'quadrant' : 'list'
        });
      } else {
        // 具体日期模式下允许所有三种模式循环切换
        const modes: ('list' | 'timeline' | 'quadrant')[] = ['list', 'timeline', 'quadrant'];
        const currentIndex = modes.indexOf(currentState.viewMode);
        const nextIndex = (currentIndex + 1) % modes.length;
        set(taskV2UnifiedStateAtom, {
          viewMode: modes[nextIndex]
        });
      }
      set(taskV2FocusedIndexAtom, -1); // 重置选中状态
    },

    // 导航到前一天（只在非特殊模式下有效）
    goToPrevDay: () => {
      const currentState = get(taskV2UnifiedStateAtom);
      if (!currentState.specialMode) {
        const prevDay = new Date(currentState.currentDate);
        prevDay.setDate(prevDay.getDate() - 1);
        set(taskV2UnifiedStateAtom, { currentDate: prevDay });
      }
      set(taskV2FocusedIndexAtom, -1); // 重置选中状态
      set(taskV2TemporaryVisibleTasksAtom, new Set()); // 清理临时状态
    },

    // 导航到后一天（只在非特殊模式下有效）
    goToNextDay: () => {
      const currentState = get(taskV2UnifiedStateAtom);
      if (!currentState.specialMode) {
        const nextDay = new Date(currentState.currentDate);
        nextDay.setDate(nextDay.getDate() + 1);
        set(taskV2UnifiedStateAtom, { currentDate: nextDay });
      }
      set(taskV2FocusedIndexAtom, -1); // 重置选中状态
      set(taskV2TemporaryVisibleTasksAtom, new Set()); // 清理临时状态
    },

    // 键盘导航：选中上一个任务（基于视觉位置的超级稳定导航）
    focusPrevTask: () => {
      const visualTasks = get(taskV2VisualTaskListAtom);
      const currentIndex = get(taskV2FocusedIndexAtom);

      if (visualTasks.length === 0) {
        set(taskV2FocusedIndexAtom, -1);
        return;
      }

      if (currentIndex < 0) {
        // 没有选中任务，选中最后一个
        set(taskV2FocusedIndexAtom, visualTasks.length - 1);
        return;
      }

      // 循环导航：到达开头时跳到末尾
      const newIndex = currentIndex <= 0 ? visualTasks.length - 1 : currentIndex - 1;
      set(taskV2FocusedIndexAtom, newIndex);
    },

    // 键盘导航：选中下一个任务（基于视觉位置的超级稳定导航）
    focusNextTask: () => {
      const visualTasks = get(taskV2VisualTaskListAtom);
      const currentIndex = get(taskV2FocusedIndexAtom);

      if (visualTasks.length === 0) {
        set(taskV2FocusedIndexAtom, -1);
        return;
      }

      if (currentIndex < 0) {
        // 没有选中任务，选中第一个
        set(taskV2FocusedIndexAtom, 0);
        return;
      }

      // 循环导航：到达末尾时跳到开头
      const newIndex = currentIndex >= visualTasks.length - 1 ? 0 : currentIndex + 1;
      set(taskV2FocusedIndexAtom, newIndex);
    },

    // 键盘导航：获取当前选中的任务（基于位置索引）
    getFocusedTask: () => {
      return get(taskV2FocusedTaskAtom); // 直接使用派生原子
    },

    // 键盘导航：重置选中状态
    resetFocus: () => {
      set(taskV2FocusedIndexAtom, -1);
    },

    // 🎉 焦点保持：什么都不用做！
    // 基于位置索引的焦点系统天然稳定，任务移动不影响焦点位置
    recordFocusPositionBeforeChange: (changedTaskId: string) => {
      // 不需要任何操作，位置索引天然稳定
      console.log('🎯 位置索引焦点系统：无需预记录，天然稳定');
    },

    maintainFocusAfterTaskChange: (changedTaskId: string) => {
      // 不需要任何操作，位置索引天然稳定
      console.log('🎯 位置索引焦点系统：无需维护，天然稳定');
    },

    // 临时状态管理：添加任务到临时可见列表
    addTemporaryVisibleTask: (taskId: string) => {
      const currentSet = get(taskV2TemporaryVisibleTasksAtom);
      const newSet = new Set(currentSet);
      newSet.add(taskId);
      set(taskV2TemporaryVisibleTasksAtom, newSet);
    },

    // 临时状态管理：清理临时可见列表
    clearTemporaryVisibleTasks: () => {
      set(taskV2TemporaryVisibleTasksAtom, new Set());
    },

    // 边界情况处理：调整焦点索引到有效范围
    adjustFocusIndex: () => {
      const currentIndex = get(taskV2FocusedIndexAtom);
      const visualTasks = get(taskV2VisualTaskListAtom);

      if (visualTasks.length === 0) {
        // 没有任务时清除焦点
        set(taskV2FocusedIndexAtom, -1);
        return;
      }

      if (currentIndex >= visualTasks.length) {
        // 索引超出范围，调整到最后一个任务
        set(taskV2FocusedIndexAtom, visualTasks.length - 1);
        return;
      }

      if (currentIndex < -1) {
        // 索引无效，重置为无焦点
        set(taskV2FocusedIndexAtom, -1);
        return;
      }

      // 索引在有效范围内，无需调整
    }
  })
);

// 当前日期的任务（派生状态）
export const taskV2CurrentTasksAtom = atom((get) => {
  const allTasks = get(tasksAtom);
  const currentDate = get(taskV2CurrentDateAtom);
  const showNoTimeTasks = get(taskV2ShowNoTimeTasksAtom);
  const specialMode = get(taskV2SpecialModeAtom);
  const temporaryVisibleTasks = get(taskV2TemporaryVisibleTasksAtom);

  if (!allTasks || allTasks.length === 0) {
    return [];
  }

  // 如果是特殊模式，使用特殊筛选逻辑
  if (specialMode) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const filteredTasks = allTasks.filter((task: Task) => {
      const taskDate = task.startAt || task.dueAt;

      if (specialMode === 'notime') {
        // 无时间任务：没有 startAt 和 dueAt 的任务
        return !taskDate;
      }

      if (specialMode === 'all') {
        // 所有任务：包括无时间任务
        return true;
      }

      if (!taskDate) {
        // 有时间的特殊模式不包含无时间任务
        return false;
      }

      const date = new Date(taskDate);
      date.setHours(0, 0, 0, 0);

      if (specialMode === 'past') {
        // 过去：昨天以前的所有任务
        return date.getTime() < yesterday.getTime();
      }

      if (specialMode === 'future') {
        // 未来：明天以后的所有任务
        return date.getTime() > tomorrow.getTime();
      }

      if (specialMode === 'recent') {
        // Recent：昨天、今天、明天的任务
        return date.getTime() >= yesterday.getTime() && date.getTime() <= tomorrow.getTime();
      }

      if (specialMode === 'all') {
        // 所有任务：返回所有有时间的任务
        return true;
      }

      return false;
    });

    // 应用时间排序（传入临时可见任务列表）
    return sortTasksByTime(filteredTasks, temporaryVisibleTasks);
  }

  // 正常的日期模式筛选逻辑 - 使用新的时间判断函数
  const filteredTasks = allTasks.filter((task: Task) => {
    // 检查是否在临时可见列表中（优先级最高）
    if (temporaryVisibleTasks.has(task.id)) {
      return true;
    }

    // 使用新的时间判断逻辑（与 fetchMem 兼容的超集）
    if (shouldTaskAppearOnDate(task, currentDate)) {
      return true;
    }

    // 无时间任务的特殊处理（保持原有逻辑）
    const taskDate = task.startAt || task.dueAt;
    if (!taskDate) {
      // 无时间任务的处理：
      // 1. 如果用户开启了显示无时间任务，且查看的是今天，则显示
      // 2. 否则不显示
      if (!showNoTimeTasks) {
        return false;
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const currentDayStart = new Date(currentDate);
      currentDayStart.setHours(0, 0, 0, 0);
      const isViewingToday = currentDayStart.getTime() === today.getTime();
      return isViewingToday;
    }

    return false;
  });

  // 应用时间排序（传入临时可见任务列表）
  return sortTasksByTime(filteredTasks, temporaryVisibleTasks);
});

// 时间轴缩放比例原子（持久化）
// 范围：0.5x - 3.0x，默认 1.0x（60px/hour）
export const taskV2TimelineScaleAtom = atomWithStorage<number>('taskV2TimelineScale', 1.0);

// 派生原子：计算实际的 pixelsPerHour
export const taskV2PixelsPerHourAtom = atom((get) => {
  const scale = get(taskV2TimelineScaleAtom);
  const basePixelsPerHour = 60; // 基础比例：每小时60像素
  return Math.round(basePixelsPerHour * scale);
});

// 按重要性分组的任务原子（TaskV2版本）
export const taskV2TasksByImportanceAtom = atom((get) => {
  const currentTasks = get(taskV2CurrentTasksAtom);

  // 定义重要性类别
  const categories = {
    vitalUrgent: { vital: true, urgent: true, label: 'vitalUrgent' },
    vital: { vital: true, urgent: false, label: 'vital' },
    urgent: { vital: false, urgent: true, label: 'urgent' },
    other: { vital: false, urgent: false, label: 'other' }
  };

  // 按类别分组任务
  const grouped = {
    vitalUrgent: currentTasks.filter(task => task.vital && task.urgent),
    vital: currentTasks.filter(task => task.vital && !task.urgent),
    urgent: currentTasks.filter(task => !task.vital && task.urgent),
    other: currentTasks.filter(task => !task.vital && !task.urgent)
  };

  // 对每个分组内的任务进行排序
  Object.keys(grouped).forEach(key => {
    grouped[key as keyof typeof grouped] = sortTasksByTime(grouped[key as keyof typeof grouped]);
  });

  return {
    categories,
    grouped
  };
});
